"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Spinner,
  Row,
  Col,
} from "reactstrap"
import {
  FaArrowLeft,
  FaLayerGroup,
  FaUsers,
  FaIdBadge,
  FaEnvelope,
  FaPhone,
  FaUserTie,
  FaUserShield,
  FaBuilding,
} from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import { toast } from "react-toastify"

import ExhibitorsService from "../../../Service/ExhibitorsService"
import type { Exhibitors } from "../Exhibitors/types"

import ParticipationTab from "./ParticipationTab"
import ProfessionalStaffTab from "./professional-staff-tab"
import type { Participation } from "./types"

interface Props {
  onAddParticipation: () => void
  onEditParticipation: (item: Participation) => void
  refreshTrigger: boolean
  setExhibitorId: React.Dispatch<React.SetStateAction<number>>
}

const InfoItem: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="d-flex align-items-center gap-2">
    <div
      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
      style={{ width: 34, height: 34, backgroundColor: "rgba(13, 110, 253, 0.08)" }}
    >
      {icon}
    </div>
    <div>
      <div className="text-muted" style={{ fontSize: "0.75rem", lineHeight: 1 }}>
        {label}
      </div>
      <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>
        {value || "—"}
      </div>
    </div>
  </div>
)

const ExhibitorsRelatedList: React.FC<Props> = ({
  onAddParticipation,
  onEditParticipation,
  refreshTrigger,
  setExhibitorId,
}) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [exhibitor, setExhibitor] = useState<Exhibitors | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<"1" | "2">("1")

  useEffect(() => {
    const fetchExhibitor = async () => {
      if (!id) {
        toast.error("Exhibitor ID is required")
        navigate(`${process.env.PUBLIC_URL}/exhibitor`)
        return
      }
      try {
        setLoading(true)
        const data = await ExhibitorsService.getById(Number(id))
        setExhibitor(data)
        setExhibitorId(data.id)
      } catch (err) {
        console.error("Failed to fetch exhibitor:", err)
        toast.error("Failed to load exhibitor details")
        navigate(`${process.env.PUBLIC_URL}/exhibitors`)
      } finally {
        setLoading(false)
      }
    }
    fetchExhibitor()
  }, [id, navigate, setExhibitorId])

  const handleBack = () => {
    navigate(`${process.env.PUBLIC_URL}/exhibitors`)
  }

  const toggle = (tab: "1" | "2") => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Spinner color="primary" />
      </div>
    )
  }

  if (!exhibitor) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="text-center">
          <h4>Exhibitor not found</h4>
          <Btn color="primary" onClick={handleBack}>
            Back to Exhibitors
          </Btn>
        </div>
      </div>
    )
  }

  const initials = exhibitor.name
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

  return (
    <div>
      <Card className="mb-4 border-0 shadow-sm">
        <CardHeader
          className="border-0"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)",
            borderTopLeftRadius: "0.5rem",
            borderTopRightRadius: "0.5rem",
          }}
        >
          <div className="d-flex align-items-center mb-3">
            <Btn color="secondary" size="sm" onClick={handleBack} className="me-3">
              <FaArrowLeft size={12} />
            </Btn>
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white me-3 flex-shrink-0"
              style={{ width: 48, height: 48, fontWeight: 700, fontSize: "1.1rem" }}
            >
              {initials || <FaBuilding />}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <h4 className="mb-0">{exhibitor.name}</h4>
              <Badge
                color={exhibitor.status ? "success" : "secondary"}
                pill
                className="fs-6 py-1 px-3"
                style={{ fontWeight: 500 }}
              >
                {exhibitor.status ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>

          <Row className="mt-2 g-3">
            <Col md="4" sm="6">
              <InfoItem icon={<FaUserTie className="text-primary" size={14} />} label="Type" value={exhibitor.type_name} />
            </Col>
            <Col md="4" sm="6">
              <InfoItem icon={<FaIdBadge className="text-primary" size={14} />} label="TIN" value={exhibitor.tin} />
            </Col>
            <Col md="4" sm="6">
              <InfoItem icon={<FaEnvelope className="text-primary" size={14} />} label="Email" value={exhibitor.email} />
            </Col>
            <Col md="4" sm="6">
              <InfoItem icon={<FaPhone className="text-primary" size={14} />} label="Phone" value={exhibitor.phone} />
            </Col>
            <Col md="4" sm="6">
              <InfoItem icon={<FaUserShield className="text-primary" size={14} />} label="Parent" value={exhibitor.parent_name} />
            </Col>
            <Col md="4" sm="6">
              <InfoItem icon={<FaUserShield className="text-primary" size={14} />} label="Created By" value={exhibitor.created_by} />
            </Col>
          </Row>
        </CardHeader>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-white border-0 p-0 pt-2 px-2">
          <Nav tabs className="border-0">
            <NavItem>
              <NavLink
                className={`px-4 py-3 fw-semibold ${activeTab === "1" ? "active text-success" : "text-muted"}`}
                onClick={() => toggle("1")}
                style={{
                  cursor: "pointer",
                  border: "none",
                  borderBottom: activeTab === "1" ? "3px solid var(--success, #28a745)" : "3px solid transparent",
                  borderTopLeftRadius: "0.375rem",
                  borderTopRightRadius: "0.375rem",
                  transition: "all 0.15s ease-in-out",
                }}
              >
                <FaLayerGroup className="me-2" /> Participations
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`px-4 py-3 fw-semibold ${activeTab === "2" ? "active text-success" : "text-muted"}`}
                onClick={() => toggle("2")}
                style={{
                  cursor: "pointer",
                  border: "none",
                  borderBottom: activeTab === "2" ? "3px solid var(--success, #28a745)" : "3px solid transparent",
                  borderTopLeftRadius: "0.375rem",
                  borderTopRightRadius: "0.375rem",
                  transition: "all 0.15s ease-in-out",
                }}
              >
                <FaUsers className="me-2" /> Staff & Badges
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody className="p-4 border-top">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <ParticipationTab
                exhibitorId={exhibitor.id}
                onAdd={onAddParticipation}
                onEdit={onEditParticipation}
                refresh={refreshTrigger}
              />
            </TabPane>
            <TabPane tabId="2">
              <ProfessionalStaffTab exhibitorId={exhibitor.id} />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  )
}

export default ExhibitorsRelatedList